import { toast } from '@heroui/react'
import { create } from 'zustand'

import { reportsApi } from '@/api/reports.api'
import i18n from '@/i18n'
import { mapApiToUi } from '@/store/reports.store'
import type { ExpenseRow,Report as UiReport } from '@/types/report.types'

export interface AnalyticsExpenseItem extends ExpenseRow {
  reportId: string;
  reportNumber: string;
  identifier: string;
}

interface AnalyticsStore {
  reports: UiReport[];
  reportItems: AnalyticsExpenseItem[];
  selectedDate: string | null;
  isLoading: boolean;
  error: string | null;
  filters: {
    driverID: string;
    from: string;
    to: string;
  };

  fetchAnalytics: () => Promise<void>;
  setFilters: (filters: Partial<AnalyticsStore['filters']>) => void;
  setSelectedDate: (date: string | null) => void;
  resetFilters: () => void;
  clearError: () => void;
}

export const useAnalyticsStore = create<AnalyticsStore>((set, get) => ({
  reports: [],
  reportItems: [],
  selectedDate: null,
  isLoading: false,
  error: null,
  filters: {
    driverID: '',
    from: '',
    to: '',
  },

  fetchAnalytics: async () => {
    const { filters } = get()
    if (!filters.driverID || !filters.from || !filters.to) {
      set({ reports: [], reportItems: [] })
      return
    }

    set({ isLoading: true, error: null })

    try {
      const response = await reportsApi.getReports({
        driverID: filters.driverID,
        from: filters.from,
        to: filters.to,
        limit: 1000, // fetch all for the period
      })

      const summaryReports = response.Reports || []
      const fullReports = await Promise.all(
        summaryReports.map(r => reportsApi.getReport((r as any).ID))
      )

      const reports = fullReports.map(mapApiToUi)
      const reportItems: AnalyticsExpenseItem[] = reports.flatMap(report =>
        (report.rows || []).map(row => ({
          ...row,
          reportId: report.id,
          reportNumber: report.reportNumber,
          identifier: report.identifier,
        })))

      set({
        reports,
        reportItems,
        isLoading: false,
      })
    } catch (err) {
      const message = (err as Error).message
      set({ error: message, isLoading: false })
      toast.danger(i18n.t('reports.toast.fetch_error'), { description: message })
    }
  },

  setFilters: newFilters => {
    set(state => ({
      filters: { ...state.filters, ...newFilters },
      selectedDate: null,
    }))
    get().fetchAnalytics()
  },

  setSelectedDate: date => set({ selectedDate: date }),

  resetFilters: () => {
    set({
      filters: {
        driverID: '',
        from: '',
        to: '',
      },
      reports: [],
      reportItems: [],
      selectedDate: null,
    })
  },

  clearError: () => set({ error: null }),
}))
